import { Box, createStyles, Stack, useMantineTheme } from '@mantine/core';
import { Text } from '@repo/foundations';

const useStyles = createStyles((theme) => ({
	wrapper: {
		position: 'absolute',
		width: 'fit-content',
		left: theme.spacing.md,
		bottom: theme.spacing.lg,
		zIndex: 5,
		backgroundColor: theme.other.getColor('surface/primary/default'),
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		borderRadius: theme.radius.md,
		padding: theme.spacing.sm,
		boxShadow: theme.shadows.sm,
	},
	legendItem: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing.xs,
		marginBottom: theme.spacing.xs,
		'&:last-child': {
			marginBottom: 0,
		},
	},
	legendBox: {
		width: 16,
		height: 16,
		borderRadius: theme.radius.sm,
		border: '1px solid',
		flexShrink: 0,
	},
}));

interface LegendItemProps {
	backgroundColor: string;
	borderColor: string;
	opacity: number;
	label: string;
	isDashed?: boolean;
	isEdge?: boolean;
}

function LegendItem({
	backgroundColor,
	borderColor,
	opacity,
	label,
	isDashed,
	isEdge,
}: LegendItemProps) {
	const { classes } = useStyles();

	if (isEdge) {
		// Render a line for edge legend items
		return (
			<div className={classes.legendItem}>
				<Box
					style={{
						width: 24,
						height: 0,
						borderTop: `${isDashed ? '2px dashed' : '2px solid'} ${borderColor}`,
						opacity,
					}}
				/>
				<Text size="xs" color="text/secondary/default">
					{label}
				</Text>
			</div>
		);
	}

	// Render a box for entity legend items
	return (
		<div className={classes.legendItem}>
			<Box
				className={classes.legendBox}
				style={{
					backgroundColor,
					borderColor,
					opacity,
					borderStyle: isDashed ? 'dashed' : 'solid',
				}}
			/>
			<Text size="xs" color="text/secondary/default">
				{label}
			</Text>
		</div>
	);
}

export function Legend() {
	const theme = useMantineTheme();
	const { classes } = useStyles();

	const legendItems: LegendItemProps[] = [
		{
			backgroundColor: theme.other.getColor('surface/primary/default'),
			borderColor: 'transparent',
			opacity: 1,
			label: 'Normal Entity',
		},
		{
			backgroundColor: theme.other.getColor('surface/warning/default'),
			borderColor: theme.other.getColor('border/warning/default'),
			opacity: 0.9,
			label: 'Archived Entity',
		},
		{
			backgroundColor: theme.other.getColor('surface/secondary/default'),
			borderColor: theme.other.getColor('border/secondary/default'),
			opacity: 0.8,
			label: 'Stale Entity',
		},
		{
			backgroundColor: 'transparent',
			borderColor: theme.other.getColor('border/tertiary/default'),
			opacity: 1,
			label: 'Normal Edge',
			isDashed: false,
			isEdge: true,
		},
		{
			backgroundColor: 'transparent',
			borderColor: theme.other.getColor('border/tertiary/default'),
			opacity: 1,
			label: 'Archived Edge',
			isDashed: true,
			isEdge: true,
		},
	];

	return (
		<Stack className={classes.wrapper} spacing="xs">
			<Text size="xs" weight="semibold" color="text/primary/default">
				Lineage Status
			</Text>
			{legendItems.map((item, index) => (
				<LegendItem key={index} {...item} />
			))}
		</Stack>
	);
}
